import { formatDisplayDateRange } from 'i18n/calendarLabels';
import { getReturnTypeNameLabel } from 'i18n/returnTypeLabel';
import type { TFunction } from 'i18next';
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { DataLogStatus } from 'store/app/enums';
import type { Mapping } from 'store/organisation/models';
import type { VatReturnLog } from 'store/vat/models';
import { PwaChip, PwaRow, PwaSection, PwaSegmented } from './ui';

export type PwaReturnsQueue = 'progress' | 'submitted';

export type PwaReturnListItem = {
  uuid: string;
  mappingId: string;
  name: string;
  jurisdiction: string;
  countryCode: string;
  period: string;
  status: DataLogStatus | string;
  by: string | null;
  sortDate: number;
};

export type PwaReturnsPageProps = {
  queue: PwaReturnsQueue;
  onQueueChange: (queue: PwaReturnsQueue) => void;
  items: PwaReturnListItem[];
  onOpenReturn: (mappingId: string, dataLogId: string) => void;
};

const COUNTRY_BADGE: Record<string, string> = {
  malta: 'MT',
  cyprus: 'CY',
  ireland: 'IE',
  'united kingdom': 'GB',
  uk: 'GB',
  netherlands: 'NL',
  'south africa': 'ZA',
  uae: 'AE',
  'isle of man': 'IM',
  jersey: 'JE',
  europe: 'EU',
  'european union': 'EU',
};

export function countryBadgeFromName(countryName: string | undefined | null): string {
  const raw = String(countryName ?? '').trim();
  if (!raw) return '—';
  const mapped = COUNTRY_BADGE[raw.toLowerCase()];
  if (mapped) return mapped;
  if (/^[A-Za-z]{2}$/.test(raw)) return raw.toUpperCase();
  return raw.slice(0, 2).toUpperCase();
}

const userDisplayName = (
  user:
    | {
        firstName?: string | null;
        lastName?: string | null;
        fullName?: string | null;
      }
    | null
    | undefined
): string | null => {
  if (!user) return null;
  const full = user.fullName?.trim();
  if (full) return full;
  const parts = [user.firstName, user.lastName].filter(Boolean);
  return parts.length ? parts.join(' ') : null;
};

export function buildPwaReturnListItems(
  mappings: Mapping[],
  vatReturnLogs: VatReturnLog[],
  countryNameByReturnTypeId: Map<string, string>,
  t: TFunction,
  language: string
): PwaReturnListItem[] {
  const mappingById = new Map(mappings.map((m) => [m.mappingId, m]));
  const items: PwaReturnListItem[] = [];

  for (const log of vatReturnLogs ?? []) {
    const mapping = mappingById.get(String(log.mappingId ?? ''));
    if (!mapping) continue;
    const status = DataLogStatus[log.status] || (log.status as string);
    if (status === DataLogStatus.CANCELLED) continue;

    const jurisdiction =
      countryNameByReturnTypeId.get(mapping.returnTypeId) ||
      countryNameByReturnTypeId.get(String(mapping.returnTypeId)) ||
      '—';
    const by =
      userDisplayName(log.submittedByUser) ||
      log.submittedBy?.trim() ||
      log.savedByUserFullName?.trim() ||
      userDisplayName(log.preparerUser) ||
      null;

    items.push({
      uuid: log.uuid,
      mappingId: mapping.mappingId,
      name: getReturnTypeNameLabel(mapping.mappingName, t, language),
      jurisdiction,
      countryCode: countryBadgeFromName(jurisdiction),
      period: formatDisplayDateRange(
        log.fromReportingDate,
        log.toReportingDate,
        'D MMM YYYY',
        language
      ),
      status,
      by,
      sortDate: new Date(
        log.submissionDate || log.generatedDate || log.toReportingDate || 0
      ).valueOf(),
    });
  }

  return items.sort((a, b) => b.sortDate - a.sortDate);
}

function statusChip(
  status: DataLogStatus | string,
  t: TFunction
): React.ReactNode {
  if (status === DataLogStatus.SUBMITTED) {
    return (
      <PwaChip tone="ok" icon="check">
        {t('main.submitted', { defaultValue: 'Submitted' })}
      </PwaChip>
    );
  }
  if (
    status === DataLogStatus.AWAITING_APPROVAL ||
    status === DataLogStatus.AWAITING_CLIENT_APPROVAL
  ) {
    return (
      <PwaChip tone="warn" icon="review">
        {String(status)}
      </PwaChip>
    );
  }
  return (
    <PwaChip tone="brand" icon="returns">
      {t('organisation.pwa-returns-in-progress', {
        defaultValue: 'In progress',
      })}
    </PwaChip>
  );
}

export const PwaReturnsPage: React.FC<PwaReturnsPageProps> = ({
  queue,
  onQueueChange,
  items,
  onOpenReturn,
}) => {
  const { t } = useTranslation();

  const filtered = React.useMemo(() => {
    if (queue === 'submitted') {
      return items.filter((item) => item.status === DataLogStatus.SUBMITTED);
    }
    return items.filter((item) => item.status !== DataLogStatus.SUBMITTED);
  }, [items, queue]);

  const counts = React.useMemo(() => {
    let progress = 0;
    let submitted = 0;
    for (const item of items) {
      if (item.status === DataLogStatus.SUBMITTED) submitted += 1;
      else progress += 1;
    }
    return { progress, submitted };
  }, [items]);

  const byJurisdiction = React.useMemo(() => {
    const map = new Map<string, PwaReturnListItem[]>();
    for (const item of filtered) {
      const key = item.jurisdiction || '—';
      const list = map.get(key) ?? [];
      list.push(item);
      map.set(key, list);
    }
    return Array.from(map.entries());
  }, [filtered]);

  return (
    <div data-shell="pwa" className="pwa pwa-returns-host scope-pwa-returns-page">
      <div className="pwa-review-toolbar">
        <PwaSegmented
          label={t('organisation.pwa-returns-status', {
            defaultValue: 'Return status',
          })}
          value={queue}
          onChange={(value) => onQueueChange(value as PwaReturnsQueue)}
          options={[
            {
              value: 'progress',
              label: t('organisation.pwa-returns-in-progress', {
                defaultValue: 'In progress',
              }),
              count: counts.progress,
            },
            {
              value: 'submitted',
              label: t('main.submitted', {
                defaultValue: 'Submitted',
              }),
              count: counts.submitted,
            },
          ]}
        />
      </div>

      {byJurisdiction.length === 0 ? (
        <PwaSection>
          <div className="pwa-list">
            <div className="pwa-row static">
              <span className="pwa-row-body">
                <span className="pwa-row-sub">
                  {queue === 'submitted'
                    ? t('submissions.no-submissions-to-display', {
                        defaultValue: 'No submissions to display',
                      })
                    : t('organisation.pwa-returns-none-in-progress', {
                        defaultValue: 'No returns in progress.',
                      })}
                </span>
              </span>
            </div>
          </div>
        </PwaSection>
      ) : (
        byJurisdiction.map(([jurisdiction, list]) => (
          <PwaSection key={jurisdiction} title={jurisdiction}>
            <div className="pwa-list">
              {list.map((item) => (
                <PwaRow
                  key={item.uuid}
                  wrap
                  chevron
                  lead={
                    <span
                      className="pwa-row-ico neutral scope-pwa-returns-country"
                      aria-hidden
                    >
                      {item.countryCode}
                    </span>
                  }
                  title={item.name}
                  sub={item.period}
                  status={
                    <>
                      {statusChip(item.status, t)}
                      {item.by ? (
                        <span className="pwa-row-meta">
                          {t('organisation.pwa-returns-by', {
                            defaultValue: 'by {{name}}',
                            name: item.by,
                          })}
                        </span>
                      ) : null}
                    </>
                  }
                  onClick={() => onOpenReturn(item.mappingId, item.uuid)}
                />
              ))}
            </div>
          </PwaSection>
        ))
      )}
    </div>
  );
};
